import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { LlmChatAttachmentDto } from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LlmChatMessageAttachment } from './LlmChatMessageAttachment';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function LlmChatAttachmentsList({
  sessionId,
  attachments,
}: {
  sessionId: string;
  attachments: LlmChatAttachmentDto[];
}) {
  const { t } = useTranslation();

  if (attachments.length === 0) {
    return (
      <p className="px-2 py-6 text-center text-sm text-muted-foreground">
        {t('llmChat.filesEmpty')}
      </p>
    );
  }

  return (
    <ul className="space-y-1">
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          <div className="flex items-center gap-2 rounded-md px-2 py-2 hover:bg-accent">
            <LlmChatMessageAttachment
              sessionId={sessionId}
              attachment={attachment}
              className="min-w-0 flex-1 text-sm no-underline"
            />
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatFileSize(attachment.size)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function LlmChatAttachmentsSidebar({
  sessionId,
  attachments,
  onClose,
  className,
}: {
  sessionId: string;
  attachments: LlmChatAttachmentDto[];
  onClose?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <aside
      className={cn(
        'flex h-40 w-full shrink-0 flex-col border-t bg-muted/30 lg:h-full lg:w-64 lg:border-l lg:border-t-0',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <h2 className="text-sm font-semibold">{t('llmChat.attachmentsTitle')}</h2>
        {onClose && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={onClose}
            aria-label={t('llmChat.attachmentsHide')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        <LlmChatAttachmentsList sessionId={sessionId} attachments={attachments} />
      </div>
    </aside>
  );
}
